/**
 * notificationRoutes — where a notification tap goes on web (PPL-531).
 *
 * ## Why this is a table and not a switch
 *
 * The old switch in `notificationService` named eleven of the thirty-odd types
 * the backend writes. Everything else fell to `'/'`, which redirects to
 * /f8chat: a tap that looked like it worked and told you nothing. Worse, three
 * of the cases it DID name pointed at `/tether`, which has not been a route in
 * `App.jsx` for months, so those fell through `*` as well.
 *
 * `ROUTES` is typed `Record<NotificationType, Router>`, so the compiler refuses
 * a type added to the union in `types/social` without a destination. That is
 * the guarantee a test cannot give: you cannot forget one.
 *
 * ## Kept free of firebase on purpose
 *
 * Nothing here imports the SDK (`NotificationType` comes in as `import type`,
 * which is erased), so the table can be bundled and run headlessly — see
 * `notificationRoutes.check.ts`, which walks every row against a sample
 * payload. The repo has no test runner; this is the substitute.
 *
 * ## What the rail entry actually carries
 *
 * `services/notificationService.js` on the backend writes an explicit object
 * literal and SILENTLY DROPS every key not in it. PPL-531 added the four that
 * were missing, so the literal now lists `personId`, `boosterId`,
 * `mediationId`, `circleId`, `circleInviteId`, `chatId`, `eventKey`,
 * `eventId`, `postId`, `channel` and `linkKey`. What a row can still be short
 * of is a WRITER, not a key. So:
 *
 *   - circle events deep-link once the sender passes an `eventId`, and land on
 *     the circle's events list when it does not;
 *   - posts arrive with no post id — post notifications go through
 *     `cacheNotification` and never become rail documents at all — so they land
 *     on the feed. Same shape as the app's `post()` helper;
 *   - a channel reply needs the id of the link's pinned f8 thread. `channel`
 *     and `linkKey` ARE listed (the gateway work added them), so the derived
 *     id below works on rail entries written today; `threadId` joins them in
 *     the server half of PPL-546.
 *
 * Every row tolerates a missing field rather than building a URL with
 * `undefined` in it.
 */

import type { NotificationType } from '../types/social';

/**
 * The subset of a rail document the routing reads. A loose shape on purpose:
 * these documents are years of accumulated writers, and a field is missing far
 * more often than it is wrong.
 */
export interface NotificationRouteInput {
  type: string;
  personId?: string | null;
  mediationId?: string | null;
  circleId?: string | null;
  /** Not written by the backend yet — read defensively, see the module note. */
  eventId?: string | null;
  chatId?: string | null;
  eventKey?: string | null;
  /** Not written by the backend yet — read defensively, see the module note. */
  postId?: string | null;
  /**
   * PPL-546 — the link's pinned f8 thread, served on a `channel_reply`. The
   * server is the only place that knows it for certain, so it wins over the
   * derivation below whenever it is present.
   */
  threadId?: string | null;
  /** The channel a `channel_reply` came in on, e.g. `whatsapp`. */
  channel?: string | null;
  /** The connected link's key, the other half of the derived thread id. */
  linkKey?: string | null;
}

type Router = (n: NotificationRouteInput) => string;

/** A non-empty string, or null. Guards every id that lands in a URL. */
function id(v: string | null | undefined): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null;
}

/* ── destinations ──────────────────────────────────────────────────────── */

/**
 * f8's chat — the web desk, and the honest home for anything web has no
 * surface for (boosters have no list here; a diagnostic entry has no screen at
 * all). It is also where `'/'` has redirected since the desk landed, so this
 * is the old default written out, one hop shorter.
 */
const DESK = '/f8chat';

/** The posts feed. `/posts` renders Home; `/talk/:id` is a single post. */
const post: Router = (n) => {
  const postId = id(n.postId);
  return postId ? `/talk/${postId}` : '/posts';
};

/**
 * A person's page, or my ppl. `extractMessageId`-style bare ids are fine here:
 * `/contacts/:contactId` takes the person id directly.
 */
const person: Router = (n) => {
  const personId = id(n.personId);
  return personId ? `/contacts/${personId}` : '/contacts';
};

/** A room, at the step the notification is about; the list when unnamed. */
const room = (suffix = ''): Router => (n) => {
  const mediationId = id(n.mediationId);
  return mediationId ? `/rooms/${mediationId}${suffix}` : '/rooms';
};

/**
 * A circle surface. The slug routes resolve an id too (`useCircleId` falls back
 * to treating the param as one), which is what makes a notification-shaped
 * deep link work at all — and what makes it work for a PRIVATE circle, whose
 * name the public slug lookup cannot see.
 */
const circle = (suffix = ''): Router => (n) => {
  const circleId = id(n.circleId);
  return circleId ? `/circles/${circleId}${suffix}` : '/circles';
};

/** A circle's event when named, its events list otherwise. */
const circleEvent: Router = (n) => {
  const circleId = id(n.circleId);
  if (!circleId) return '/circles';
  const eventId = id(n.eventId);
  return eventId
    ? `/circles/${circleId}/events/${eventId}`
    : `/circles/${circleId}/events`;
};

/**
 * PPL-546 — a channel reply belongs on the link's pinned f8 thread.
 *
 * The served `threadId` is authoritative; `${channel}_${linkKey}` is literally
 * the id the gateway's links module names the pinned thread with, so the two
 * cannot disagree. The derivation is what makes rail entries written before
 * the server half deep-link at all. Both halves are guarded: a link-less entry
 * arrives with a present-and-null `threadId` and a channel but no key, and
 * must not become a bare `whatsapp_`. With no id either way, settings →
 * channels is where the connected link shows (PPL-464), and that stays the
 * honest landing rather than a `/f8chat?thread=undefined`.
 */
const channelReply: Router = (n) => {
  const served = id(n.threadId);
  const channel = id(n.channel);
  const linkKey = id(n.linkKey);
  const threadId = served || (channel && linkKey ? `${channel}_${linkKey}` : null);
  // Encoded on the way out. A link key today is a truncated base64url digest
  // (the gateway hashes the external id precisely so no phone number is ever
  // embedded), which needs no escaping — but a SERVED id is the server's to
  // shape, and a bare `+` in a query string reads back as a space.
  return threadId ? `${DESK}?thread=${encodeURIComponent(threadId)}` : '/settings';
};

/* ── the table ─────────────────────────────────────────────────────────── */

export const ROUTES: Record<NotificationType, Router> = {
  /* people — the conversation-starter writers and the per-person nudges.
     All of them name a person; the list is the fallback, never f8chat. */
  personal: person,
  background: person,
  personal_message_triggered_push_ai: person,
  // Onboarding's batch names no person, so the list is the honest target.
  'conversation starters': () => '/contacts',
  // The 10am birthday flow. Matched calendar birthdays reuse this type and
  // carry a person too (`RADAR_NOTIFICATION_TYPE`).
  birthday: person,
  // Legacy rail documents only, and some of them carry no person.
  message: person,
  // Your own page — a holiday nudge is about you, not about a contact.
  holiday: () => '/profile',

  /* calendar life (PPL-277/340). Web has no life surface — ContactDetail says
     so in as many words — so the person is the nearest true destination, and
     for capture it is the RIGHT one: the log-a-hangout modal lives there. */
  premeet_brief: person,
  postevent_capture: person,
  // The unmatched radar ping carries no person by design. My ppl is where you
  // would add them, which is the actual next step; the app's life agenda has
  // no web counterpart.
  calendar_birthday: () => '/contacts',

  /* messages. `chat_message` never reaches the rail today (it is a push and a
     cache entry only), but the push handler and the cache share this table. */
  chat_message: (n) => {
    const chatId = id(n.chatId);
    return chatId ? `/messages/${chatId}` : '/messages';
  },
  // A request's durable home is the requests section of the messages list —
  // not the conversation, which is exactly what has not been accepted yet.
  message_request: () => '/messages',

  /* posts. `tether_*` are legacy names on live documents: do not rename them
     without a data migration. All three used to point at `/tether`, which is
     not a route. */
  tether_combined_notification: () => '/posts',
  tether_reply: post,
  tether_interaction: post,
  // PPL-460 — f8's reply to a channel message it could not deliver in the
  // window. PPL-546: that reply lives on the link's pinned f8 thread, and
  // F8Chat now opens one from `?thread=`, so the tap lands on the conversation
  // it is about. Settings → channels stays the fallback for a payload that
  // names no thread, never posts.
  channel_reply: channelReply,

  /* circles. Every one of these carries `circleId`. */
  circle_event: circleEvent,
  circle_event_reminder: circleEvent,
  circle_message: circle('/chat'),
  // An invite is acted on from the circle's own page.
  circle_invite: circle(),
  circle_invite_accepted: circle(),
  circle_invite_declined: circle(),

  /* rooms. RoomDetail is the catch-all on purpose: it is the canonical status
     and next-step view, and forwards to the dedicated screen where one
     exists. `mediation_completed` is gone — no backend writer has ever sent
     it (grepped across cloud-functions). */
  mediation_checkin: room('/checkin'),
  mediation_invite: room('/invite'),
  mediation_reaction: room('/room'),
  mediation_picture_request: room('/room'),
  mediation_room_open: room('/room'),
  mediation_accepted: room(),
  mediation_declined: room(),
  mediation_prep_ended: room(),
  mediation_deleted: room(),
  mediation_paused: room(),
  mediation_resumed: room(),
  mediation_ended_summary: room(),
  mediation_rules_confirmed: room(),
  mediation_room_ready: room(),

  /* the review queue. `/insights` IS the write-back review surface on web
     (PPL-265) — the app's "life → review" segment by another name. */
  writeback_review: () => '/insights',

  /* intelligent push. Two of these are a prompt to add people, which my ppl
     answers; the rest have no web surface and fall to the desk below. */
  add_people_prompt_ai: () => '/contacts',
  intelligent_push_people_fallback: () => '/contacts',

  /* no web surface. Boosters have no list here, a tip is f8 talking, and
     'push notification' is a marker the app ignores outright. */
  'relationship tips': () => DESK,
  boosters: () => DESK,
  'push notification': () => DESK,
};

/**
 * Where tapping this notification should go.
 *
 * An unknown `type` — a writer newer than this build, or one of the diagnostic
 * entries the intelligent-push flow writes — lands on the desk rather than
 * nowhere.
 */
export function getNotificationLink(n: NotificationRouteInput): string {
  const route = ROUTES[n.type as NotificationType];
  return route ? route(n) : DESK;
}
